import {InputAdornment, SxProps, TextField, TextFieldProps, Theme} from "@mui/material";
import React, {ChangeEventHandler} from "react";
import {autoCompleteOff, FieldError} from "@/lib/utils";

/** Module-level constant — no prop/state dependencies, so it never changes between renders. */
const EMPHASIZED_SX: SxProps<Theme> = [
    (theme) => ({
        "& .MuiOutlinedInput-notchedOutline": {borderWidth: 2, borderColor: theme.palette.primary.main},
        "&:hover .MuiOutlinedInput-notchedOutline": {borderWidth: 2, borderColor: theme.palette.primary.main},
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: theme.palette.primary.main
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: theme.palette.primary.main
        },
        "& .MuiOutlinedInput-root.Mui-readOnly .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: theme.palette.primary.main
        },
        "& .MuiInputLabel-root": {fontSize: "1.05rem"}
    })
];

/**
 * Props for the StringInputField component.
 *
 * @property {string} [name] - The name of the input field.
 * @property {string} label - The label for the input field.
 * @property {ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>} [onChange] - The handler for change events.
 * @property {FieldError} [error] - The error object for the input field.
 * @property {boolean} [disabled] - If true, disables the input field.
 * @property {boolean} [readOnly] - If true, renders the field read-only without the disabled look (normal text
 *   contrast). The field cannot be edited, but it is not greyed out. Input changes are blocked in the onChange
 *   handler. Use this instead of `disabled` when you want the value to remain clearly legible.
 * @property {Omit<React.HTMLAttributes<HTMLElement>, "readOnly" | "color">} [inputProps] - Additional props for the
 *   input element. The `readOnly` attribute is intentionally excluded — use the dedicated `readOnly` prop instead to
 *   prevent accidental overrides.
 * @property {boolean} [activatePrefix] - If true, activates the prefix.
 * @property {string} [prefixValue] - The prefix value to display.
 * @property {boolean} [multiline] - If true, the input field will be multiline.
 * @property {string | null} [value] - The current value of the input field.
 * @property {number} [maxChars] - The maximum number of characters allowed.
 * @property {RegExp} [matchRule] - The regular expression rule to match the input value.
 * @property {SxProps<Theme>} [sx] - The style props for the input field.
 * @property {TextFieldProps["type"]} [type] - The type of the input field.
 * @property {TextFieldProps["autoComplete"]} [autoComplete] - The autocomplete attribute for the input field.
 * @property {TextFieldProps["ref"]} [textFieldRef] - The ref for the input field.
 * @property {React.ReactNode} [startAdornment] - An optional node rendered as a start adornment inside the input.
 * @property {boolean} [emphasized] - When true, applies a thicker border and a slightly larger label to make the
 *   field visually more prominent. Defaults to false (unchanged behaviour). The emphasised sx is merged with any
 *   externally supplied sx via the MUI array form, so both can coexist safely.
 */
interface StringInputFieldProps {
    name?: string;
    label?: string;
    onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
    error?: FieldError;
    disabled?: boolean;
    readOnly?: boolean;
    inputProps?: Omit<React.HTMLAttributes<HTMLElement>, "readOnly" | "color">;
    activatePrefix?: boolean;
    prefixValue?: string;
    multiline?: boolean;
    value?: string | null;
    maxChars?: number;
    matchRule?: RegExp;
    sx?: SxProps<Theme>;
    type?: TextFieldProps["type"];
    autoComplete?: TextFieldProps["autoComplete"];
    textFieldRef?: TextFieldProps["ref"];
    endAdornment?: React.ReactNode;
    startAdornment?: React.ReactNode;
    variant?: TextFieldProps["variant"];
    helperText?: string;
    minRows?: number;
    placeholder?: string;
    emphasized?: boolean;
}

/**
 * StringInputField component.
 *
 * @param {StringInputFieldProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered component.
 */
const StringInputField: React.FC<StringInputFieldProps> = ({
    name,
    label,
    onChange,
    error,
    disabled,
                                                               readOnly,
    inputProps,
    activatePrefix,
    prefixValue,
    multiline,
    value,
    maxChars,
    matchRule,
    sx,
    type,
    autoComplete = autoCompleteOff,
    textFieldRef,
    endAdornment,
    startAdornment,
    variant = "outlined",
    helperText,
    minRows,
                                                               placeholder,
                                                               emphasized = false
}: StringInputFieldProps): React.ReactElement => {
    const onChangeInt = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (disabled || readOnly) {
            return;
        }
        if (onChange) {
            if (
                e.target?.value &&
                ((maxChars && maxChars < e.target.value.length) || (matchRule && !matchRule.test(e.target.value)))
            ) {
                return;
            }
            onChange(e);
        }
    };

    const helperTextEx = `${helperText ?? ""}${helperText && error?.message ? " - " : ""}${error?.message ?? ""}`;

    // MUI sx supports an array of style objects — the array form is a valid SxProps<Theme> at runtime.
    // The spread widens the element-union type beyond what tsc accepts without a cast at this boundary.
    const resolvedSx: SxProps<Theme> = emphasized ? ([...EMPHASIZED_SX, sx ?? {}] as SxProps<Theme>) : (sx ?? {});

    let resolvedStartAdornment: React.ReactNode;
    if (activatePrefix) {
        resolvedStartAdornment = <InputAdornment position="start">{prefixValue}</InputAdornment>;
    } else if (startAdornment) {
        resolvedStartAdornment = <InputAdornment position="start">{startAdornment}</InputAdornment>;
    }

    return (
        <TextField
            inputRef={textFieldRef}
            label={label}
            fullWidth
            value={value ?? ""}
            onChange={onChangeInt}
            error={!!error}
            helperText={helperTextEx}
            name={name}
            disabled={disabled}
            multiline={multiline}
            maxRows={multiline ? 5 : undefined}
            minRows={multiline ? (minRows ?? 1) : undefined}
            variant={variant}
            size="small"
            sx={resolvedSx}
            type={type}
            placeholder={placeholder}
            slotProps={{
                input: {
                    startAdornment: resolvedStartAdornment,
                    endAdornment: endAdornment ? (
                        <InputAdornment position="end">{endAdornment}</InputAdornment>
                    ) : undefined,
                    ...inputProps,
                    readOnly
                },
                inputLabel: {
                    shrink: true
                }
            }}
            autoComplete={autoComplete}
        />
    );
};

export default StringInputField;
